import { __awaiter, __generator, __read } from "tslib";
import React, { useEffect, useState } from "react";
import Modal from "@adsk/alloy-react-modal";
import Button, { LinkButton } from "@adsk/alloy-react-button";
import { useTranslations } from "../utils";
import { TemplateCreateType, SubmitError } from "../shared/types";
import { ProjectTemplateCreateSelectTypeModal } from "./components/ProjectTemplateCreateSelectTypeModal";
import { CancelConfirmation } from "../shared/components/CancelConfirmation";
import { ProjectTemplateCreateDataInputModal } from "./components/ProjectTemplateCreateDataInputModal";
import {
  DuplicateNameError,
  InvalidNameError,
  NameTooLongError,
} from "../shared/errors";
import { AnalyticsEvents } from "../shared/constants";
import {
  FooterButton,
  ModalBody,
  ModalComponent,
  ModalHeader,
} from "../shared/SharedCreateTemplateModal.styles";
export var CreateTemplateModal = function (props) {
  var T = useTranslations().T;
  // UI State
  var _a = __read(useState(1), 2),
    step = _a[0],
    setStep = _a[1];
  var _b = __read(useState(TemplateCreateType.Blank), 2),
    templateCreateType = _b[0],
    setTemplateCreateType = _b[1];
  var _c = __read(useState(""), 2),
    templateName = _c[0],
    setTemplateName = _c[1];
  var _d = __read(useState(null), 2),
    selectedAccountId = _d[0],
    setSelectedAccountId = _d[1];
  var _e = __read(useState(null), 2),
    selectedProjectId = _e[0],
    setSelectedProjectId = _e[1];
  var _f = __read(useState(false), 2),
    isSubmitting = _f[0],
    setIsSubmitting = _f[1];
  var _g = __read(useState(false), 2),
    isConfirming = _g[0],
    setIsConfirming = _g[1];
  var _h = __read(useState(undefined), 2),
    inputError = _h[0],
    setInputError = _h[1];
  // Computed properties
  var canSubmit = [
    // Submit conditions for a blank template
    templateCreateType === TemplateCreateType.Blank &&
      selectedAccountId &&
      templateName.length > 0,
    // Submit conditions for a template from project
    templateCreateType === TemplateCreateType.FromProject &&
      selectedAccountId &&
      selectedProjectId &&
      templateName.length > 0,
  ].some(function (condition) {
    return condition;
  });
  // Side effects
  // Side effect - Set the selectedAccountId if there is only a single account
  useEffect(
    function () {
      if (!props.accounts.loading) {
        if (props.accounts.data.length === 1) {
          setSelectedAccountId(props.accounts.data[0].id);
        }
      }
    },
    [props.accounts.loading, props.accounts.data]
  );
  // Side effect - When the template name changes, dismiss the inputError
  useEffect(
    function () {
      setInputError(undefined);
    },
    [templateName]
  );
  // Side effect - When open becomes false, clear the state
  useEffect(
    function () {
      if (!props.open) {
        // Clear state
        setStep(1);
        setTemplateCreateType(TemplateCreateType.Blank);
        setTemplateName("");
        setSelectedAccountId(null);
        setSelectedProjectId(null);
        setIsSubmitting(false);
        setIsConfirming(false);
        setInputError(undefined);
      }
    },
    [props.open]
  );
  // Helper functions
  var onSubmit = function () {
    return __awaiter(void 0, void 0, void 0, function () {
      var createTemplateRequest, error_1;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            setIsSubmitting(true);
            if (templateCreateType === TemplateCreateType.FromProject) {
              createTemplateRequest = {
                name: templateName,
                accountId: selectedAccountId,
                type: templateCreateType,
                projectId: selectedProjectId,
              };
            } else {
              createTemplateRequest = {
                name: templateName,
                accountId: selectedAccountId,
                type: templateCreateType,
              };
            }
            _a.label = 1;
          case 1:
            _a.trys.push([1, 3, 4, 5]);
            return [4 /*yield*/, props.onSubmit(createTemplateRequest)];
          case 2:
            _a.sent();
            props.track &&
              props.track(AnalyticsEvents.createTemplateModal.submit.success);
            return [3 /*break*/, 5];
          case 3:
            error_1 = _a.sent();
            // Catch the error and evaluate if it matches an error that is can be remediated by the
            // user.  If so, set the appropriate inputError.
            if (error_1 instanceof DuplicateNameError) {
              setInputError(SubmitError.DuplicateName);
              props.track &&
                props.track(
                  AnalyticsEvents.createTemplateModal.submit.duplicateNameError
                );
            } else if (error_1 instanceof InvalidNameError) {
              setInputError(SubmitError.InvalidName);
              props.track &&
                props.track(
                  AnalyticsEvents.createTemplateModal.submit.invalidNameError
                );
            } else if (error_1 instanceof NameTooLongError) {
              setInputError(SubmitError.NameTooLong);
              props.track &&
                props.track(
                  AnalyticsEvents.createTemplateModal.submit.nameTooLongError
                );
            } else {
              setInputError(SubmitError.Other);
              props.track &&
                props.track(
                  AnalyticsEvents.createTemplateModal.submit.otherError
                );
            }
            return [3 /*break*/, 5];
          case 4:
            setIsSubmitting(false);
            return [7 /*endfinally*/];
          case 5:
            return [2 /*return*/];
        }
      });
    });
  };
  var onClose = function () {
    // If there is input, require confirmation
    if (templateName || selectedAccountId || selectedProjectId) {
      // Note: selectedProjectId should never be defined when selectedAccountId is defined, but
      // it is specified here for safety.
      setIsConfirming(true);
    }
    // If there is no user input, it is safe to directly call onClose
    else {
      props.onClose();
    }
  };
  return React.createElement(
    React.Fragment,
    null,
    React.createElement(CancelConfirmation, {
      open: props.open && isConfirming,
      onClose: function () {
        return setIsConfirming(false);
      },
      onConfirm: function () {
        // Note: When the user confirms, _both_ modals need to be dismissed
        setIsConfirming(false);
        props.onClose();
      },
    }),
    props.open &&
      !isConfirming &&
      React.createElement(
        ModalComponent,
        {
          "data-testid": "CreateTemplateModal",
          open: props.open && !isConfirming,
          onClose: onClose,
        },
        React.createElement(
          ModalHeader,
          { "data-testid": "CreateTemplateModal.Header" },
          T.translate("genericStrings.createTemplate"),
          React.createElement(Modal.Close, {
            "data-testid": "CreateTemplateModal.CloseButton",
            onClick: function () {
              props.track &&
                props.track(
                  AnalyticsEvents.createTemplateModal.closeModalFromHeader
                );
              onClose();
            },
          })
        ),
        React.createElement(
          ModalBody,
          { "data-testid": "CreateTemplateModal.Body" },
          step === 1 &&
            React.createElement(ProjectTemplateCreateSelectTypeModal, {
              selectedProjectType: templateCreateType,
              setSelectedProjectType: setTemplateCreateType,
              track: props.track,
            }),
          step === 2 &&
            React.createElement(ProjectTemplateCreateDataInputModal, {
              name: templateName,
              accountsLoading: props.accounts.loading,
              projectsLoading: props.projects.loading,
              onNameChange: setTemplateName,
              accounts: props.accounts.data,
              onSelectAccount: function (accountId) {
                setSelectedAccountId(accountId);
                setSelectedProjectId(null);
              },
              onSelectProject: setSelectedProjectId,
              templateCreateType: templateCreateType,
              isSubmitting: isSubmitting,
              selectedAccountId: selectedAccountId,
              projects: props.projects.data,
              inputError: inputError,
              track: props.track,
            })
        ),
        React.createElement(
          Modal.Footer,
          null,
          step === 1 &&
            React.createElement(
              React.Fragment,
              null,
              React.createElement(
                FooterButton,
                null,
                React.createElement(
                  LinkButton,
                  {
                    "data-testid": "CreateTemplateModal.Footer.CancelButton",
                    onClick: function () {
                      onClose();
                      props.track &&
                        props.track(
                          AnalyticsEvents.createTemplateModal.cancelButton
                        );
                    },
                  },
                  T.translate("navigation.cancel")
                )
              ),
              React.createElement(
                Button,
                {
                  "data-testid": "CreateTemplateModal.Footer.NextButton",
                  variant: "primary",
                  onClick: function () {
                    setStep(2);
                    props.track &&
                      props.track(
                        AnalyticsEvents.createTemplateModal.nextButton
                      );
                  },
                },
                T.translate("navigation.next")
              )
            ),
          step === 2 &&
            React.createElement(
              React.Fragment,
              null,
              React.createElement(
                FooterButton,
                null,
                React.createElement(
                  LinkButton,
                  {
                    "data-testid": "CreateTemplateModal.Footer.BackButton",
                    onClick: function () {
                      setStep(1);
                      props.track &&
                        props.track(
                          AnalyticsEvents.createTemplateModal.backButton
                        );
                    },
                  },
                  T.translate("navigation.back")
                )
              ),
              React.createElement(
                Button,
                {
                  "data-testid": "CreateTemplateModal.Footer.SubmitButton",
                  variant: "primary",
                  loading: isSubmitting,
                  disabled: !canSubmit || isSubmitting || !!inputError,
                  onClick: function () {
                    props.track &&
                      props.track(
                        AnalyticsEvents.createTemplateModal.submitButton
                      );
                    onSubmit();
                  },
                },
                T.translate("genericStrings.createTemplate")
              )
            )
        )
      )
  );
};
